// Giphy-backed GIF picker for the composer. Mounted as an absolutely
// positioned popover above the GIF button — same shape as the existing
// EmojiShortcodePopover. Picking a GIF fires onPick(url, alt) and closes.

import { useEffect, useRef, useState } from 'react'
import { Loader2, Search, X } from 'lucide-react'
import { usePage } from '@inertiajs/react'
import { cn } from '@/lib/utils'
import { searchGifs, trendingGifs, type GiphyGif } from '@/lib/giphy'
import type { SharedProps } from '@/types/shared'

interface GifPickerProps {
  onClose: () => void
  onPick: (url: string, alt: string) => void
  /** Controlled query — when provided the internal search input is hidden
   *  (the caller's textarea is acting as the search). */
  query?: string
}

export function GifPicker({ onClose, onPick, query: externalQuery }: GifPickerProps) {
  const apiKey = usePage<SharedProps>().props.giphy_api_key
  const [internalQuery, setInternalQuery] = useState('')
  const query = externalQuery ?? internalQuery
  const controlled = externalQuery !== undefined
  const [gifs, setGifs] = useState<GiphyGif[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(apiKey ? null : 'missing-key')
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    if (!apiKey) {
      setLoading(false)
      return
    }
    let cancelled = false
    if (debounceRef.current) clearTimeout(debounceRef.current)

    debounceRef.current = setTimeout(() => {
      setLoading(true)
      setError(null)
      const promise = query.trim() ? searchGifs(apiKey, query.trim()) : trendingGifs(apiKey)
      promise
        .then(results => { if (!cancelled) setGifs(results) })
        .catch((e: Error) => { if (!cancelled) setError(e.message) })
        .finally(() => { if (!cancelled) setLoading(false) })
    }, query ? 250 : 0)

    return () => {
      cancelled = true
      if (debounceRef.current) clearTimeout(debounceRef.current)
    }
  }, [apiKey, query])

  return (
    <div
      className="absolute bottom-full left-0 mb-2 w-[380px] bg-card border border-border rounded-lg shadow-xl overflow-hidden z-30"
      role="dialog"
      aria-label="GIF picker"
    >
      {controlled ? (
        <div className="flex items-center gap-2 px-3 py-2 border-b border-border text-xs text-muted-foreground">
          <Search className="size-4 shrink-0" />
          <span className="flex-1 truncate">
            {query ? <>Searching GIPHY for <span className="text-foreground">{query}</span></> : 'Trending on GIPHY — keep typing to search'}
          </span>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="p-1 rounded hover:bg-muted text-muted-foreground cursor-pointer"
          >
            <X className="size-4" />
          </button>
        </div>
      ) : (
        <div className="flex items-center gap-2 px-3 py-2 border-b border-border">
          <Search className="size-4 text-muted-foreground shrink-0" />
          <input
            autoFocus
            value={internalQuery}
            onChange={(e) => setInternalQuery(e.target.value)}
            placeholder="Search GIPHY…"
            className="flex-1 bg-transparent outline-none text-sm placeholder:text-muted-foreground"
          />
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="p-1 rounded hover:bg-muted text-muted-foreground cursor-pointer"
          >
            <X className="size-4" />
          </button>
        </div>
      )}

      <div className="relative">
        {loading && (
          <div className="absolute inset-x-0 top-0 flex justify-center py-2 pointer-events-none">
            <Loader2 className="size-4 animate-spin text-muted-foreground" />
          </div>
        )}

        {error && (
          <p className="text-xs text-destructive px-3 py-6 text-center">
            {error === 'missing-key'
              ? 'Giphy isn\'t configured. Add giphy.api_key to credentials and reload.'
              : `Search failed — ${error}`}
          </p>
        )}

        {!error && (
          <div className="grid grid-cols-3 gap-1 p-2 max-h-[320px] overflow-y-auto">
            {gifs.map(gif => (
              <button
                key={gif.id}
                type="button"
                onMouseDown={(e) => { e.preventDefault(); onPick(gif.gifUrl, gif.description) }}
                className={cn(
                  'aspect-video rounded-md overflow-hidden bg-muted',
                  'hover:ring-2 hover:ring-primary cursor-pointer transition-all'
                )}
                title={gif.description}
              >
                <img
                  src={gif.previewUrl}
                  alt={gif.description}
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </button>
            ))}
            {!loading && gifs.length === 0 && (
              <p className="col-span-3 text-center text-xs text-muted-foreground py-6">
                {query ? `No GIFs for "${query}"` : 'No results'}
              </p>
            )}
          </div>
        )}
      </div>

      <div className="px-3 py-1.5 border-t border-border text-[10px] text-muted-foreground text-right">
        Powered by GIPHY
      </div>
    </div>
  )
}
